import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import type { ManagerPerm } from '../types'
import type { KitchenBellKey } from '../utils/kitchenBell'
import { setPrintingEnabled } from '../utils/printing'
import { planOf, type Plan, type PlanPeriod } from '../plans'
import { resolveTenantId, rememberTenantId } from '../services/tenant'
import { cachedPayStatus, fetchPayStatus, isPayLate, isPayLocked, type PayStatus } from '../services/payStatus'
import { authBackend, AuthError } from '../services/auth'
import { AUTH_ERRORS } from '../services/auth/messages'
import {
  defaultSettings,
  subscribeTenantSettings,
  type TenantSettings,
} from '../services/auth/tenantSettings'
import {
  ROLE_LABELS,
  type AdminUser,
  type FeatureKey,
  type Features,
  type NetworkPrinter,
  type ReceiptSettings,
  type Role,
  type TenantType,
  type LoyaltySettings,
} from '../types'

interface AuthContextValue {
  isAuthenticated: boolean
  admin: AdminUser | null
  tenantId: string | null
  /** Human-readable name for the receipt header; falls back to the id. */
  tenantName: string
  /** İşletmenin yüklediği logo; yoksa null, yerine Adissoo işareti. */
  tenantLogo: string | null
  /** Percent off for "İndirimli", configured per institution. */
  studentDiscountPercent: number
  /** Discount rates offered behind the "Diğer" button. */
  discounts: number[]
  /** Role names as this institution calls them. */
  roleLabels: Record<Role, string>
  /** What the stall slip shows, as configured in Ayarlar. */
  receiptSettings: ReceiptSettings
  /** What the till's own slip shows when payment is taken. */
  cashReceiptSettings: ReceiptSettings
  /** Ağ yazıcıları (Ayarlar > Yazıcılar); boşsa fiş tarayıcıdan basılır. */
  printers: NetworkPrinter[]
  /**
   * Fiş yazıcısı kullanıyor mu (Ayarlar > Yazıcılar). Kapalıyken yazdır
   * düğmeleri gizli, hiçbir şey basılmaz.
   */
  usePrinter: boolean
  /** Mutfak ekranının yeni fiş zili (Ayarlar > Mutfak ekranları). */
  kitchenBell: KitchenBellKey
  /** Müşteri sadakat kuralı (damga modülü). */
  loyalty: LoyaltySettings
  /** What kind of institution this is; decides nothing on its own. */
  tenantType: TenantType
  /**
   * The optional parts this institution has been given. Read through
   * `hasFeature` rather than directly, an absent flag is off, and every
   * call site would otherwise repeat that.
   */
  features: Features
  /** Free-trial end (ms), null for a permanent institution. */
  trialEndsAt: number | null
  /** The trial has run out: the till takes no new orders. */
  trialExpired: boolean
  /** Abonelik ödemesi 5 günden fazla gecikti: salt okunur (abonelik-kilit.sql). */
  payLocked: boolean
  /** Ödeme günü geçti, henüz kapanmadı. */
  payLate: boolean
  /** Ödeme günü ve kapanış günü. */
  payStatus: PayStatus | null
  /** Kurumun paketi (plans.ts), paketsizse null. */
  plan: Plan | null
  /** Paketin ödeme dönemi, bilinmiyorsa null. */
  planPeriod: PlanPeriod | null
  /**
   * Kasiyer ve garson sınırı (paketten); null sınırsız. Patron ve mutfak
   * hesapları sayılmaz.
   */
  staffLimit: number | null
  /** Contact number, `+90…`, or null. */
  contactPhone: string | null
  hasFeature: (key: FeatureKey) => boolean
  isOwner: boolean
  /** Müdür (3 Kas 2026): kasiyerin her şeyi, üstüne patronun verdiği izinler. */
  isManager: boolean
  /**
   * Patron ya da bu izni verilmiş müdür. Patrona özel ekranların (Menü,
   * Rapor, Ayarlar) kapısı; Hesabım ve kurumu silme bunun dışında, yalnız isOwner.
   */
  can: (perm: ManagerPerm) => boolean
  /** Ayarlar sayfasına girebilir: patron, ya da personel veya ayar izni olan müdür. */
  canOpenSettings: boolean
  /** Waiters open tickets; only these roles may take money. */
  canSettle: boolean
  /** Mutfak personeli: yalnız Mutfak ekranını görür. */
  isKitchen: boolean
  /** Kiosk tableti: yalnız müşteri sipariş ekranını görür (30 Eki 2026). */
  isKiosk: boolean
  /**
   * Whether this person may move an open ticket to another table, or merge
   * two of them.
   *
   * The counter always may: correcting a bill written on the wrong table is
   * its daily work, and putting that behind a switch leaves a ticket nobody
   * can fix. For a waiter it is the owner's call, per person.
   */
  canMoveTable: boolean
  /** Kasa ve patron her zaman; garson patron yetki verdiyse. */
  canEditPrice: boolean
  /** Sipariş alabilir: kasa ve patron her zaman; garson patron kapatmadıysa (yok = açık). */
  canOrder: boolean
  /** Sipariş Takip: kurumda modül açık ve patron bu garsona vermiş. */
  canTrack: boolean
  /**
   * Administers the whole system rather than one institution: creates
   * institutions and appoints their owners.
   */
  isSuperadmin: boolean
  loading: boolean      // true while we're resolving the initial auth state
  /**
   * Kurum ayarları (özellik bayrakları dahil) henüz gelmedi. Bayrağa bakıp
   * yönlendiren bir ekran bunu beklemeli: gelmeden önce her özellik kapalı
   * görünüyor.
   */
  settingsLoading: boolean
  submitting: boolean   // true during a login call
  error: string | null
  /** What to do about it, when the failure has actionable advice. */
  errorHint: string | null
  /**
   * Short code beside the message.
   *
   * So someone at a counter can report "ERR02" down a phone instead of
   * describing a screen. Stable and never reused, see services/auth/messages.
   */
  errorCode: string | null
  /** Resolves true when the account is the system administrator's. */
  /**
   * `tenant`, when given, is the institution the identity belongs to (from
   * the login screen's lookup). It wins over the address: a person signing
   * in on another kurum's "/…/giris" belongs to their own, not that one.
   */
  login: (username: string, pin: string, tenant?: string) => Promise<boolean>
  /** Aims the app at another institution before signing in. */
  switchTenant: (tenantId: string) => void
  logout: () => Promise<void>
  /**
   * Mails a reset link to an owner's real address.
   */
  sendPasswordReset?: (email: string) => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

/** Turns whatever was thrown into something readable, with its code. */
function describe(err: unknown): { title: string; hint: string | null; code: string | null } {
  if (err instanceof AuthError) {
    const known = err.code ? Object.values(AUTH_ERRORS).find((m) => m.code === err.code) : null
    return {
      title: err.message,
      hint: known?.hint ?? null,
      code: err.code ?? null,
    }
  }
  const fallback = AUTH_ERRORS['GIRIS-0']
  return {
    title: err instanceof Error ? err.message : fallback.title,
    hint: fallback.hint ?? null,
    code: fallback.code,
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminUser | null>(null)
  const [isSuperadmin, setIsSuperadmin] = useState(false)
  const [tenantId, setTenantId] = useState<string | null>(() => resolveTenantId())
  const [settings, setSettings] = useState<TenantSettings>(() =>
    defaultSettings(resolveTenantId() ?? ''),
  )
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errorHint, setErrorHint] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)

  /**
   * Re-subscribes whenever the institution changes, not just on mount.
   *
   * The membership check inside runs against whichever tenant this
   * subscription was opened with. Pinned to mount, it kept asking about the
   * tenant the device happened to remember when the app started, so signing
   * in under a different kurum code succeeded at the password step and was
   * then thrown out by a membership query aimed at the wrong institution,
   * landing back on the login screen with no explanation.
   */
  useEffect(() => {
    return authBackend.subscribe(tenantId, (session) => {
      if (!session) {
        setAdmin(null)
        setIsSuperadmin(false)
        setLoading(false)
        return
      }

      setAdmin({
        uid: session.uid,
        email: session.email,
        username: session.username,
        displayName: session.displayName,
        role: session.role,
        perms: session.perms,
      })
      setIsSuperadmin(session.isSuperadmin)
      setLoading(false)
    })
  }, [tenantId])

  // Live, not a one-off read: the owner changes the discount on the menu
  // screen and the till has to pick it up without anyone signing out. Also
  // serves the receipt header.
  //
  // Hangi kurumun ayarlarının geldiği ayrıca tutuluyor: gelene kadar
  // `features` boş ve her özellik kapalı görünüyor. Cari'de yenileyen patron
  // bu yüzden "cari kapalı" sayılıp ana sayfaya atılıyordu.
  const [settingsFor, setSettingsFor] = useState<string | null>(null)
  useEffect(() => {
    if (!tenantId || !admin) return
    return subscribeTenantSettings(tenantId, (next) => {
      setSettings(next)
      setSettingsFor(tenantId)
    })
  }, [tenantId, admin])

  // Abonelik ödeme durumu (2 Kas 2026): girişte ve yarım saatte bir.
  const [payStatus, setPayStatus] = useState<PayStatus | null>(null)
  useEffect(() => {
    if (!tenantId || !admin) return
    setPayStatus(cachedPayStatus(tenantId))
    let live = true
    const read = () => fetchPayStatus(tenantId).then((s) => live && setPayStatus(s))
    void read()
    const i = window.setInterval(read, 30 * 60_000)
    return () => {
      live = false
      window.clearInterval(i)
    }
  }, [tenantId, admin])

  // App.print bu kurumda basılıp basılmayacağını buradan okuyor.
  useEffect(() => setPrintingEnabled(settings.usePrinter), [settings.usePrinter])

  const login = async (username: string, pin: string, tenant?: string) => {
    setSubmitting(true)
    setError(null)
    setErrorHint(null)
    setErrorCode(null)
    try {
      const tid = tenant ?? resolveTenantId()
      if (!tid) {
        const m = AUTH_ERRORS['GIRIS-5']
        throw new AuthError('no-tenant', m.title, m.code)
      }

      const result = await authBackend.signIn(tid, username, pin)
      rememberTenantId(tid)
      return result?.isSuperadmin === true
    } catch (err) {
      const d = describe(err)
      setError(d.title)
      setErrorHint(d.hint)
      setErrorCode(d.code)
      throw err
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * Points the app at a different institution, before anyone signs in.
   *
   * The login screen calls this the moment a kurum code checks out, not
   * after the password step: the membership subscription has to already be
   * watching the right institution when the session arrives, or it asks
   * about the old one, finds nothing, and signs the person straight back
   * out. Doing it afterwards instead tore the subscription down mid-login
   * and bounced them to an empty form.
   */
  const switchTenant = (next: string) => {
    rememberTenantId(next)
    setTenantId(next)
  }

  const sendPasswordReset = authBackend.sendPasswordReset
    ? (email: string) => authBackend.sendPasswordReset!(email)
    : undefined

  const logout = async () => {
    await authBackend.signOut()
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: admin !== null,
        admin,
        tenantId,
        tenantName: settings.name || tenantId || 'Gıda Etkinliği',
        tenantLogo: settings.logoUrl,
        tenantType: settings.type,
        features: settings.features,
        trialEndsAt: settings.trialEndsAt,
        plan: planOf(settings.plan),
        planPeriod: settings.planPeriod === 'aylik' || settings.planPeriod === 'yillik' ? settings.planPeriod : null,
        staffLimit: planOf(settings.plan)?.staff ?? null,
        contactPhone: settings.contactPhone,
        trialExpired: settings.trialEndsAt !== null && Date.now() >= settings.trialEndsAt,
        payLocked: !isSuperadmin && isPayLocked(payStatus),
        payLate: !isSuperadmin && isPayLate(payStatus),
        payStatus,
        // An absent flag is off. Written once here so no call site has to
        // remember that a feature nobody has been given is simply missing
        // from the object rather than present and false.
        hasFeature: (key) => settings.features[key] === true,
        discounts: settings.discounts,
        receiptSettings: settings.receipt,
        cashReceiptSettings: settings.cashReceipt,
        printers: settings.printers,
        usePrinter: settings.usePrinter,
        kitchenBell: settings.kitchenBell,
        loyalty: settings.loyalty,
        studentDiscountPercent: settings.studentDiscountPercent,
        roleLabels: ROLE_LABELS,
        isOwner: admin?.role === 'owner',
        isManager: admin?.role === 'manager',
        can: (perm) => admin?.role === 'owner' || (admin?.role === 'manager' && admin.perms?.[perm] === true),
        canOpenSettings:
          admin?.role === 'owner' ||
          (admin?.role === 'manager' && (admin.perms?.settings === true || admin.perms?.staff === true)),
        canSettle: admin?.role === 'owner' || admin?.role === 'manager' || admin?.role === 'cashier',
        isKitchen: admin?.role === 'kitchen',
        isKiosk: admin?.role === 'kiosk',
        canMoveTable:
          admin?.role === 'owner' ||
          admin?.role === 'manager' ||
          admin?.role === 'cashier' ||
          admin?.perms?.moveTable === true,
        canEditPrice:
          admin?.role === 'owner' ||
          admin?.role === 'manager' ||
          admin?.role === 'cashier' ||
          admin?.perms?.editPrice === true,
        canOrder: admin?.role !== 'waiter' || admin?.perms?.order !== false,
        canTrack:
          settings.features.takip === true &&
          admin?.role === 'waiter' &&
          admin?.perms?.track === true,
        isSuperadmin,
        loading,
        settingsLoading: admin !== null && settingsFor !== tenantId,
        submitting,
        error,
        errorHint,
        errorCode,
        login,
        switchTenant,
        logout,
        sendPasswordReset,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
